// 한글/일본어/중국어가 섞였으면 수식이 아니라 산문이다.
const PROSE_SCRIPT = /[\u3040-\u30FF\u3131-\u318E\u4E00-\u9FFF\uAC00-\uD7A3]/;
// 큰따옴표·백틱은 수식에 쓰이지 않는다 — 스프레드시트 수식(`COUNTIFS(A:A;"PRD-*")`)과
// 코드 조각(`$a = 10`) 신호다. `;` 는 LaTeX 에서도 쓰이므로(`\;`) 기준으로 쓰지 않는다.
const NON_MATH_PUNCTUATION = /["`]/;
// `\frac`·`\sin` 같은 제어 시퀀스나 `\#`·`\&` 같은 이스케이프된 특수문자가 있으면
// 작성자가 LaTeX 을 의도한 것이 확실하다.
const LATEX_COMMAND = /\\(?:[a-zA-Z]|[#&%_{}])/;
// 단어 세 개 이상이 띄어쓰기로 이어지면 산문이다(`dalam referensi sel`, `80 miliar dan`).
// 두 단어(`$sin theta$`)나 붙어 있는 글자 묶음(`$abc$`·`$NaCl$`)은 정상 수식일 수 있다.
const PROSE_WORDS = /[A-Za-z]{3,}\s+[A-Za-z]{2,}\s+[A-Za-z]{2,}/;
// 제어 시퀀스 없이 등장하는 `#`(매크로 인자)·`&`(정렬)은 Temml 이 거부한다.
const BARE_SPECIAL = /[#&]/;
const MATH_LIKE_NUMERIC = /[A-Za-z\\^_=+\-*/<>()[\]{}|]/;

/**
 * `$...$` 안의 내용을 LaTeX 으로 파싱할지 판정한다. 입력은 HTML escape 를 푼 평문이다.
 *
 * `$` 는 수식 에디터가 심은 구분자(ZWSP / `<math-inline>`)가 아니라 작성자가 친
 * 평문일 수 있다. 근거 없이 파싱하면 두 방향으로 깨진다 — Temml 이 throw 해서
 * 로그만 쌓이거나(`$을 썻습니다 #$`), 더 나쁘게는 파싱이 "성공"해 `$` 가 사라진
 * 채 엉뚱한 수식이 렌더된다(`$A$1` → 기울임 `A`). 그래서 수식으로 볼 근거가
 * 있을 때만 참을 돌려준다. QUIZ-APP-748 참고.
 */
export const isInlineMathLike = (content: string): boolean => {
  if (
    !content ||
    /^\s/.test(content) ||
    /\s$/.test(content) ||
    content.includes('\n')
  ) {
    return false;
  }

  // `\text{넓이}` 처럼 한글이 섞인 정상 수식이 있으므로 산문 판정보다 먼저 본다.
  if (LATEX_COMMAND.test(content)) {
    return true;
  }

  if (PROSE_SCRIPT.test(content) || NON_MATH_PUNCTUATION.test(content)) {
    return false;
  }

  if (BARE_SPECIAL.test(content) || PROSE_WORDS.test(content)) {
    return false;
  }

  return /^\d/.test(content) ? MATH_LIKE_NUMERIC.test(content) : true;
};

/**
 * 스프레드시트 절대 참조(`$A$1`, `$AB$12:$C$9`)인지 판정한다. 열 이름 한두 글자는
 * 수식 변수와 구분이 안 되므로 대문자 열 이름 + 닫는 `$` 뒤 행 번호 모양으로 가린다.
 * 소문자(`$x$2개`)는 수식 뒤에 숫자가 붙은 것으로 본다.
 */
export const isSpreadsheetAbsoluteReference = (
  content: string,
  charAfterClosing: string | undefined,
): boolean =>
  /^[A-Z]{1,3}$/.test(content) &&
  charAfterClosing !== undefined &&
  /\d/.test(charAfterClosing);
